/**
 * ! Note for reference :
 * * Redux itself cannot retain then store values on reload of page, as a solution to it
 * * we use redux-persist package, which helps to restore the previous state values.
 */

import { configureStore, combineReducers } from "@reduxjs/toolkit";
import titleReducer from "@features/titleSlice";
import isExcelUploadReducer from "@features/isExcelUploadSlice";
import toggledReducer from "@features/toggledSlice";
import {
    clientListReducer,
    activeClientIdReducer,
    activeClientGroupIdReducer,
    clientTagsListReducer,
    tagTypeListReducer,
    isAddTagTypeFormSubmittedReducer,
    clientSubGroupIdReducer,
    clientIdOnChannelChangeReducer,
    tagTypeListLoadingReducer,
} from "@features/clientDataSlice";
import {
    isGenerateMediaPlanLoadingReducer,
    loadingReducer,
    mediaPlanListLoadingReducer,
} from "@features/loaderSlice";
import dashboardLoadingReducer from "@features/dashboardLoaderSlice";
import toastReducer from "@features/toastSlice";
import clientSelectionReducer from "@features/clientSelectionSlice";
import {
    chartsDataReducer,
    funnelChartsReducer,
    activeKeyReducer,
} from "@features/chartsDataSlice";
import {
    isSessionExpiredReducer,
    warningsModalReducer,
    customModalReducer,
    createCampaignModalReducer,
    actionModalReducer,
    trendsModalReducer,
    trendsTitleReducer,
    trendsDataOneReducer,
    trendsDataTwoReducer,
    customModalTitleReducer,
    productModalTypeReducer,
    asinIdReducer,
    keywordsReducer,
    submoduleElementReducer,
    moduleActionReducer,
    ruleReducer,
    isEditReducer,
    isDaypartingEditReducer,
    isAllPortfoliosSelectedReducer,
    isAllCampaignsSelectedReducer,
    isAllAdgroupsSelectedReducer,
    isAllPfmDataSelectedReducer,
    rulesApiReducer,
    bulkTaxonomyDisplayReducer,
    dayPartingDisplayReducer,
    daypartingDataObjectReducer,
    daypartingEditCampaignDataReducer,
    daypartingCardCheckedDataReducer,
    showAdminSettingsDisplayReducer,
    showCreateKeywordsReviewDisplayReducer,
    showRequestAlertReducer,
    overwriteRequestReducer,
    bulkActionModalReducer,
    updateBidOrBudgetModalDisplayReducer,
    dailyBudgetModalDisplayReducer,
    dailyBudgetModalDataReducer,
    dailyBudgetReviewModalDisplayReducer,
    dailyBudgetUpdatePayloadReducer,
    dailyBudgetUpdateResponseReducer,
    addToExactModalDispReducer,
    keywordModalTypeReducer,
    searchTermCampaignsReducer,
    campaignPortfolioDataReducer,
    addAsExactDataReducer,
    isSearchTermExactReducer,
    addToAdgroupModalDataReducer,
    dateChangeModalDisplayReducer,
    cpmModalDisplayReducer,
    isCampaignStateActionReducer,
    campaignStateActionResponseReducer,
    addGoalsModalDisplayReducer,
    goalDataValuesReducer,
    goalEditConfigReducer,
    goalModalTitleReducer,
    isGoalFormSubmittedReducer,
    goalListStatusTypeReducer,
    deepDiveModalDisplayReducer,
    addBulkTagsModalDisplayReducer,
    bulkProductMappingModalDisplayReducer,
    bulkKeywordMappingModalDisplayReducer,
    biddingModalReducer,
    isRuleSaveButtonDisabledReducer,
    addMediaPlanModalDisplayReducer,
    showNewMediaPlanDisplayReducer,
    mediaPlanConfigValuesReducer,
    bulkPlacementModalDisplayReducer,
    productMappingFilterModelReducer,
    keywordMappingFilterModelReducer,
    bulkPfmMappingCountReducer,
    bulkKeywordMappingCountReducer,
    apply1DsMappingModalDisplayReducer,
    confirmModalReducer,
    showRevstackPopupReducer,
    apply1DsKeywordMappingModalDisplayReducer,
} from "@features/modalSlice";
import activeTabReducer from "@features/activeTabSlice";
import activeModuleReducer from "@features/activeModuleSlice";
import {
    portfoliosAggregatedDataReducer,
    campaignsAggregatedDataReducer,
    adgroupsAggregatedDataReducer,
    keywordsAggregatedDataReducer,
    productsAggregatedDataReducer,
} from "@features/workersDataSlice";
import {
    isRuleFiltersAppliedReducer,
    portfolioFiltersReducer,
    campaignFiltersReducer,
    adgroupFiltersReducer,
    keywordFiltersReducer,
    productFiltersReducer,
    placementFiltersReducer,
    mainGridFiltersReducer,
    poGridFiltersReducer,
    caGridFiltersReducer,
    adGridFiltersReducer,
    pdGridFiltersReducer,
    checkedNodeReducer,
    poCheckedNodeReducer,
    caCheckedNodeReducer,
    adCheckedNodeReducer,
    pdCheckedNodeReducer,
    poFilterCountReducer,
    caFilterCountReducer,
    adFilterCountReducer,
    pdFilterCountReducer,
} from "@features/filtersSlice";

import {
    enableDaypartingReducer,
    enableBuyboxReducer,
    enableRevstackReducer,
    enableKeywordRecommendationReducer,
    isBuyboxUpdatedReducer,
    clientTimezoneReducer,
    buyboxEnabledDataReducer,
    doPreferredSellersExistReducer,
    userCountryDetailsReducer,
    focusedAsinsReducer,
    goalsConfigReducer,
} from "@features/adminSettingsSlice";

import { globalGridApiReducer } from "@features/gridApiSlice";

import {
    portfoliosReducer,
    campaignsReducer,
    adgroupsReducer,
    keywordReducer,
    placementsReducer,
    productsReducer,
    createKeywordsReviewDataReducer,
    createKeywordsDataReducer,
    manualRecommendationReducer,
    autoRecommendationReducer,
    revinitiRecommendationReducer,
    budgetRecommendationReducer,
    placementRecommendationReducer,
    productAnalyticsDataReducer,
    reportsDataReducer,
    reportTypesDataReducer,
    selectedReportDataReducer,
    recordTagsDataReducer,
    showDeltaValuesReducer,
    goalDataReducer,
    suggestedNegKwsDataReducer,
    exstingNegKwsDataReducer,
    suggestedNegKwsLoadingReducer,
    existingNegKwsLoadingReducer,
    tagTypeAggregatedDataReducer,
    tagTypeOptionsReducer,
    selectedTagTypeOptionReducer,
    campaignLevelTotalSpendReducer,
    tagTypeLevelTotalSpendReducer,
    funnelResponseReducer,
    productMappingDataReducer,
    generateMediaPlanDataReducer,
    mediaPlanGridApiMapReducer,
    mediaPlanningListReducer,
    mediaPlanBudgetReducer,
    mediaPlanEditConfigReducer,
    isMediaPlanEditReducer,
    isMediaPlanCreateReducer,
    showMediaPlanChangeLogsReducer,
} from "@features/gridDataSlice";

import { refreshTokenMetaDataReducer } from "@features/tokenDataSlice";

import {
    allSysCampaignIdReducer,
    allPortfolioIdReducer,
    allSysAdgroupIdReducer,
} from "@features/selectAllGridDataSlice";
import {
    keywordsSelectionDataReducer,
    productsSelectionDataReducer,
    productAdgroupsSelectionDataReducer,
    negativeKeywordsSelectionDataReducer,
    overviewKeywordsSelectionDataReducer,
    campaignsSelectionDataReducer,
    historyLogsSelectionDataReducer,
    adgroupSelectionDataReducer,
    productMappingSelectionDataReducer,
    keywordMappingSelectionDataReducer,
    keywordMappingAllPagesSelectedReducer,
    productMappingAllPagesSelectedReducer,
    selectedMediaPlanDataReducer,
    selectedDateRangeReducer,
    productMappingHeaderSelectionReducer,
    keywordMappingHeaderSelectionReducer
} from "@features/selectedDataSlice";
import {
    channelReducer,
    channelListReducer,
    channelWalletBalanceReducer,
    channelCurrencySymbolReducer,
    channelDomainReducer,
    revstackChannelReducer,
    revstackChannelsListReducer,
} from "@features/channelSlice";
import requestMappingReducer from "./features/requestMappingSlice";
import sessionStorage from "redux-persist/es/storage/session";
import { persistReducer } from "redux-persist";
import {
    exactLoaderReducer,
    negativeLoaderReducer,
} from "./features/bulkActionBtnConfigSlice";
import { userDataReducer } from "./features/userDataSlice";
import { errorStatusCodeReducer } from "./features/errorStatusCodeSlice";
import sidebarReducer from "@/features/sidebarSlice";
import {
    mainFiltersReducer,
    revstackLayoutFiltersReducer,
    revstackWidgetDataReducer,
    mainFiltersDataReducer,
    aiInsightsPayloadReducer,
    aiInsightsDataReducer,
    drillDownFiltersReducer,
    revstackActiveTabReducer,
    isAiInsightsLoadingReducer,
    revstackDashboardTabsReducer,
    trendsFiltersReducer,
    relativeFiltersReducer,
    skuMainFiltersReducer,
    filtersFromUrlReducer,
    additionalFiltersReducer
} from "./features/revstackWidgetsSlice";
import campaignCreationDisplayReducer from "@features/campaignCreationSlice";
import clustersReducer from "./features/clustersSlice";

const persistConfig = {
    key: "root",
    version: 1,
    storage: sessionStorage,
    blacklist: [
        "portfoliosData",
        "campaignsData",
        "recordTagsData",
        "adgroupsData",
        "keywordsData",
        "placementsData",
        "productsData",
        "reportsData",
        "reportTypesData",
        "selectedReportData",
        "portfoliosGridApi",
        "campaignsGridApi",
        "adgroupsGridApi",
        "keywordsGridApi",
        "productsGridApi",
        "isRuleFiltersApplied",
        "portfolioFilters",
        "campaignFilters",
        "adgroupFilters",
        "keywordFilters",
        "productFilters",
        "placementFilters",
        "activeFilters",
        "isSessionExpired",
        "warningsModalDisp",
        "actionModalDisp",
        "customModalDisp",
        "bulkActionModalDisp",
        "addToExactModalDisp",
        "createCampaignModalDisp",
        "trendsModalDisp",
        "customModalTitle",
        "keywordModalType",
        "productModalType",
        "trendsTitle",
        "trendsDataOne",
        "trendsDataTwo",
        "mainGridFilters",
        "checkedNode",
        "bulkTaxonomyDisplay",
        "showAdminSettingsDisplay",
        "showCreateKeywordsReviewDisplay",
        "dayPartingDisplay",
        "updateCpmModalDisplay",
        "addGoalsModalDisplay",
        "goalsConfig",
        "goalDataValues",
        "goalData",
        "goalEditConfig",
        "goalModalTitle",
        "isGoalFormSubmitted",
        "goalListStatusType",
        "allSysCampaignId",
        "showRequestAlert",
        "overwriteRequest",
        "allPortfolioId",
        "allSysAdgroupId",
        "campaignsSelectionData",
        "adgroupSelectionData",
        "keywordsSelectionData",
        "productsSelectionData",
        "productAdgroupsSelectionData",
        "negativeKeywordsSelectionData",
        "overviewKeywordsSelectionData",
        "historyLogsSelectionData",

        "poCheckedNode",
        "caCheckedNode",
        "adCheckedNode",
        "pdCheckedNode",

        "poFilterCount",
        "caFilterCount",
        "adFilterCount",
        "pdFilterCount",

        "funnelChartsRef",
        "activeKey",
        "isExcelUpload",

        "portfoliosAggregatedData",
        "campaignsAggregatedData",
        "adgroupsAggregatedData",
        "keywordsAggregatedData",
        "productsAggregatedData",

        "filtersFromUrl",

        "chartsData",
        "toastData",
        "mainGridFilters",
        "poGridFilters",
        "caGridFilters",
        "adGridFilters",
        "pdGridFilters",
        "funnelData",

        "daypartingDataObject",
        "isDaypartingEdit",
        "isAllCampaignsSelected",
        "isAllPortfoliosSelected",
        "isAllAdgroupsSelected",
        "isAllPfmDataSelected",
        "daypartingEditCampaignData",
        "daypartingCardCheckedData",
        "isBuyboxUpdated",
        "buyboxEnabledData",
        "focusedAsins",
        "clients",
        "activeClientId",
        "clientIdOnChannelChange",
        "activeClientGroupId",
        "hasIOTAccess",
        "hasReportBuilderAccess",
        "clientTagsList",
        "doPreferredSellersExist",
        "globalGridApi",
        "updateBidOrBudgetModalDisplay",
        "dailyBudgetModalDisplay",
        "bulkPlacementModalDisplay",
        "dailyBudgetModalData",
        "dailyBudgetReviewModalDisplay",
        "campaignConfigModalDisplay",
        "isCampaignStateAction",
        "campaignStateActionResponse",
        "dailyBudgetUpdatePayload",
        "dailyBudgetUpdateResponse",
        "campaignPortfolioData",
        "clientCampaignPortfolioData",
        "manualRecommendationData",
        "autoRecommendationData",
        "revinitiRecommendationData",
        "budgetRecommendationData",
        "placementRecommendationData",
        "productAnalyticsData",
        "enableDayparting",
        "enableBuybox",
        "enableRevstack",
        "channelList",
        // "channel",
        "channelWalletBalance",
        "userCountryDetails",

        "channelCurrencySymbol",
        "channelDomain",
        "searchTermCampaigns",
        "campaignPortfolioData",

        "suggestedNegKwsData",
        "suggestedNegKwsLoading",
        "existingNegKwsData",
        "existingNegKwsLoading",

        "exactLoader",
        "negativeLoader",
        "addAsExactData",
        "isSearchTermExact",
        "addToAdgroupModalData",
        "deepDiveModalDisplay",

        "tagTypeAggregatedData",
        "tagTypeOptions",
        "selectedTagTypeOption",
        "campaignLevelTotalSpend",
        "tagTypeLevelTotalSpend",
        "tagTypeList",
        "isAddTagTypeFormSubmitted",

        "funnelResponse",
        "addBulkTagsModalDisplay",
        "refreshTokenMetaData",
        "productMappingData",
        "bulkProductMappingModalDisplay",
        "bulkKeywordMappingModalDisplay",
        "productMappingSelectionData",
        "keywordMappingSelectionData",
        "keywordMappingAllPagesSelected",
        "productMappingAllPagesSelected",
        "biddingModal",
        "userData",
        "isRuleSaveButtonDisabled",
        "addMediaPlanModalDisplay",
        "showNewMediaPlanDisplay",
        "mediaPlanConfigValues",
        "isGenerateMediaPlanLoading",
        "generateMediaPlanData",
        "mediaPlanGridApiMap",
        "mediaPlanningList",
        "mediaPlanListLoading",
        "selectedMediaPlanData",
        "mediaPlanBudget",
        "mediaPlanEditConfig",
        "isMediaPlanEdit",
        "isMediaPlanCreate",
        "showMediaPlanChangeLogs",
        "errorStatusCode",

        "productMappingFilterModel",
        "keywordMappingFilterModel",
        "bulkPfmMappingCount",
        "apply1DsMappingModalDisplay",
        "apply1DsKeywordMappingModalDisplay",
        "tagTypeListLoading",
        "confirmModalDisplay",
        "drilldownFilters",
        "trendsFilters",
        "relativeFilters",
        "revstackLayoutFilters",
        "revstackDashboardWidgetData",
        "revstackDrilldownWidgetData",
        "revstackDrilldownTrendsWidgetData",
        "mainFiltersData",
        "aiInsightsPayload",
        "revstackChannelsList",
        "aiInsightsData",
        "showRevstackPopup",
        "revstackActiveTab",
        "isAiInsightsLoading",
        "revstackDashboardTabs",
        "revstackWidgetData",
        "clusters",
        "campaignCreationDisplay",
        "skuMainFilters",
    ],
};

const rootReducer = combineReducers({
    title: titleReducer,
    isExcelUpload: isExcelUploadReducer,
    toggle: toggledReducer,
    clients: clientListReducer,
    clientIdOnChannelChange: clientIdOnChannelChangeReducer,
    activeClientId: activeClientIdReducer,
    activeClientGroupId: activeClientGroupIdReducer,
    clientTagsList: clientTagsListReducer,
    isLoading: loadingReducer,
    isDashboardLoading: dashboardLoadingReducer,
    toastData: toastReducer,
    isSessionExpired: isSessionExpiredReducer,
    warningsModalDisp: warningsModalReducer,
    actionModalDisp: actionModalReducer,
    customModalDisp: customModalReducer,
    bulkActionModalDisp: bulkActionModalReducer,
    addToExactModalDisp: addToExactModalDispReducer,
    createCampaignModalDisp: createCampaignModalReducer,
    trendsModalDisp: trendsModalReducer,
    customModalTitle: customModalTitleReducer,
    keywordModalType: keywordModalTypeReducer,
    productModalType: productModalTypeReducer,
    trendsTitle: trendsTitleReducer,
    chartsData: chartsDataReducer,
    activeKey: activeKeyReducer,
    funnelChartsRef: funnelChartsReducer,
    activeTab: activeTabReducer,
    activeModule: activeModuleReducer,
    trendsDataOne: trendsDataOneReducer,
    trendsDataTwo: trendsDataTwoReducer,
    asinId: asinIdReducer,
    keywords: keywordsReducer,
    searchTermCampaigns: searchTermCampaignsReducer,

    portfolioFilters: portfolioFiltersReducer,
    campaignFilters: campaignFiltersReducer,
    adgroupFilters: adgroupFiltersReducer,
    keywordFilters: keywordFiltersReducer,
    productFilters: productFiltersReducer,
    placementFilters: placementFiltersReducer,

    submoduleElement: submoduleElementReducer,
    moduleAction: moduleActionReducer,
    rule: ruleReducer,
    isEdit: isEditReducer,
    isRuleSaveButtonDisabled: isRuleSaveButtonDisabledReducer,
    isDaypartingEdit: isDaypartingEditReducer,
    isAllCampaignsSelected: isAllCampaignsSelectedReducer,
    isAllPortfoliosSelected: isAllPortfoliosSelectedReducer,
    isAllAdgroupsSelected: isAllAdgroupsSelectedReducer,
    isAllPfmDataSelected: isAllPfmDataSelectedReducer,
    daypartingDataObject: daypartingDataObjectReducer,
    daypartingEditCampaignData: daypartingEditCampaignDataReducer,
    daypartingCardCheckedData: daypartingCardCheckedDataReducer,
    rulesApi: rulesApiReducer,
    bulkTaxonomyDisplay: bulkTaxonomyDisplayReducer,
    showAdminSettingsDisplay: showAdminSettingsDisplayReducer,
    showCreateKeywordsReviewDisplay: showCreateKeywordsReviewDisplayReducer,
    dayPartingDisplay: dayPartingDisplayReducer,
    showRequestAlert: showRequestAlertReducer,
    overwriteRequest: overwriteRequestReducer,

    portfoliosData: portfoliosReducer,
    campaignsData: campaignsReducer,
    recordTagsData: recordTagsDataReducer,
    adgroupsData: adgroupsReducer,
    keywordsData: keywordReducer,
    placementsData: placementsReducer,
    productsData: productsReducer,
    createKeywordsReviewData: createKeywordsReviewDataReducer,
    createKeywordsData: createKeywordsDataReducer,
    globalGridApi: globalGridApiReducer,
    productAnalyticsData: productAnalyticsDataReducer,
    reportsData: reportsDataReducer,
    reportTypesData: reportTypesDataReducer,
    selectedReportData: selectedReportDataReducer,
    showDeltaValues: showDeltaValuesReducer,

    isRuleFiltersApplied: isRuleFiltersAppliedReducer,
    mainGridFilters: mainGridFiltersReducer,
    poGridFilters: poGridFiltersReducer,
    caGridFilters: caGridFiltersReducer,
    adGridFilters: adGridFiltersReducer,
    pdGridFilters: pdGridFiltersReducer,

    checkedNode: checkedNodeReducer,
    poCheckedNode: poCheckedNodeReducer,
    caCheckedNode: caCheckedNodeReducer,
    adCheckedNode: adCheckedNodeReducer,
    pdCheckedNode: pdCheckedNodeReducer,

    poFilterCount: poFilterCountReducer,
    caFilterCount: caFilterCountReducer,
    adFilterCount: adFilterCountReducer,
    pdFilterCount: pdFilterCountReducer,

    filtersFromUrl: filtersFromUrlReducer,

    portfoliosAggregatedData: portfoliosAggregatedDataReducer,
    campaignsAggregatedData: campaignsAggregatedDataReducer,
    adgroupsAggregatedData: adgroupsAggregatedDataReducer,
    keywordsAggregatedData: keywordsAggregatedDataReducer,
    productsAggregatedData: productsAggregatedDataReducer,

    enableClientSelection: clientSelectionReducer,
    refreshTokenMetaData: refreshTokenMetaDataReducer,

    allSysCampaignId: allSysCampaignIdReducer,
    enableDayparting: enableDaypartingReducer,
    enableBuybox: enableBuyboxReducer,
    enableRevstack: enableRevstackReducer,
    enableKeywordRecommendation: enableKeywordRecommendationReducer,
    isBuyboxUpdated: isBuyboxUpdatedReducer,
    userCountryDetails: userCountryDetailsReducer,
    clientSubGroupId: clientSubGroupIdReducer,
    clientTimezone: clientTimezoneReducer,
    allPortfolioId: allPortfolioIdReducer,
    allSysAdgroupId: allSysAdgroupIdReducer,
    campaignsSelectionData: campaignsSelectionDataReducer,
    adgroupSelectionData: adgroupSelectionDataReducer,
    keywordsSelectionData: keywordsSelectionDataReducer,
    overviewKeywordsSelectionData: overviewKeywordsSelectionDataReducer,
    productsSelectionData: productsSelectionDataReducer,
    productAdgroupsSelectionData: productAdgroupsSelectionDataReducer,
    negativeKeywordsSelectionData: negativeKeywordsSelectionDataReducer,
    historyLogsSelectionData: historyLogsSelectionDataReducer,
    buyboxEnabledData: buyboxEnabledDataReducer,
    focusedAsins: focusedAsinsReducer,
    doPreferredSellersExist: doPreferredSellersExistReducer,
    channel: channelReducer,
    channelList: channelListReducer,
    channelWalletBalance: channelWalletBalanceReducer,
    channelCurrencySymbol: channelCurrencySymbolReducer,
    channelDomain: channelDomainReducer,
    updateBidOrBudgetModalDisplay: updateBidOrBudgetModalDisplayReducer,
    dailyBudgetModalDisplay: dailyBudgetModalDisplayReducer,
    bulkPlacementModalDisplay: bulkPlacementModalDisplayReducer,
    dailyBudgetModalData: dailyBudgetModalDataReducer,
    dailyBudgetReviewModalDisplay: dailyBudgetReviewModalDisplayReducer,
    campaignConfigModalDisplay: dateChangeModalDisplayReducer,
    isCampaignStateAction: isCampaignStateActionReducer,
    campaignStateActionResponse: campaignStateActionResponseReducer,
    dailyBudgetUpdatePayload: dailyBudgetUpdatePayloadReducer,
    dailyBudgetUpdateResponse: dailyBudgetUpdateResponseReducer,
    campaignPortfolioData: campaignPortfolioDataReducer,

    manualRecommendationData: manualRecommendationReducer,
    autoRecommendationData: autoRecommendationReducer,
    revinitiRecommendationData: revinitiRecommendationReducer,
    budgetRecommendationData: budgetRecommendationReducer,
    placementRecommendationData: placementRecommendationReducer,
    requestMapping: requestMappingReducer,
    suggestedNegKwsData: suggestedNegKwsDataReducer,
    suggestedNegKwsLoading: suggestedNegKwsLoadingReducer,
    existingNegKwsData: exstingNegKwsDataReducer,
    existingNegKwsLoading: existingNegKwsLoadingReducer,

    exactLoader: exactLoaderReducer,
    negativeLoader: negativeLoaderReducer,
    addAsExactData: addAsExactDataReducer,
    isSearchTermExact: isSearchTermExactReducer,
    addToAdgroupModalData: addToAdgroupModalDataReducer,
    updateCpmModalDisplay: cpmModalDisplayReducer,
    addGoalsModalDisplay: addGoalsModalDisplayReducer,
    goalsConfig: goalsConfigReducer,
    goalDataValues: goalDataValuesReducer,
    goalData: goalDataReducer,
    goalEditConfig: goalEditConfigReducer,
    goalModalTitle: goalModalTitleReducer,
    isGoalFormSubmitted: isGoalFormSubmittedReducer,
    goalListStatusType: goalListStatusTypeReducer,
    deepDiveModalDisplay: deepDiveModalDisplayReducer,

    tagTypeAggregatedData: tagTypeAggregatedDataReducer,
    tagTypeOptions: tagTypeOptionsReducer,
    selectedTagTypeOption: selectedTagTypeOptionReducer,
    campaignLevelTotalSpend: campaignLevelTotalSpendReducer,
    tagTypeLevelTotalSpend: tagTypeLevelTotalSpendReducer,
    tagTypeList: tagTypeListReducer,
    isAddTagTypeFormSubmitted: isAddTagTypeFormSubmittedReducer,

    funnelResponse: funnelResponseReducer,
    addBulkTagsModalDisplay: addBulkTagsModalDisplayReducer,
    productMappingData: productMappingDataReducer,
    bulkProductMappingModalDisplay: bulkProductMappingModalDisplayReducer,
    bulkKeywordMappingModalDisplay: bulkKeywordMappingModalDisplayReducer,
    productMappingSelectionData: productMappingSelectionDataReducer,
    keywordMappingSelectionData: keywordMappingSelectionDataReducer,
    keywordMappingAllPagesSelected: keywordMappingAllPagesSelectedReducer,
    productMappingAllPagesSelected: productMappingAllPagesSelectedReducer,
    productMappingHeaderSelection: productMappingHeaderSelectionReducer,
    keywordMappingHeaderSelection: keywordMappingHeaderSelectionReducer,
    biddingModal: biddingModalReducer,
    userData: userDataReducer,

    addMediaPlanModalDisplay: addMediaPlanModalDisplayReducer,
    showNewMediaPlanDisplay: showNewMediaPlanDisplayReducer,
    mediaPlanConfigValues: mediaPlanConfigValuesReducer,
    isGenerateMediaPlanLoading: isGenerateMediaPlanLoadingReducer,
    generateMediaPlanData: generateMediaPlanDataReducer,
    mediaPlanGridApiMap: mediaPlanGridApiMapReducer,
    mediaPlanningList: mediaPlanningListReducer,
    mediaPlanListLoading: mediaPlanListLoadingReducer,
    selectedMediaPlanData: selectedMediaPlanDataReducer,
    mediaPlanBudget: mediaPlanBudgetReducer,
    mediaPlanEditConfig: mediaPlanEditConfigReducer,
    isMediaPlanEdit: isMediaPlanEditReducer,
    isMediaPlanCreate: isMediaPlanCreateReducer,
    showMediaPlanChangeLogs: showMediaPlanChangeLogsReducer,

    errorStatusCode: errorStatusCodeReducer,
    sidebar: sidebarReducer,
    productMappingFilterModel: productMappingFilterModelReducer,
    keywordMappingFilterModel: keywordMappingFilterModelReducer,
    bulkPfmMappingCount: bulkPfmMappingCountReducer,
    bulkKeywordMappingCount: bulkKeywordMappingCountReducer,
    apply1DsMappingModalDisplay: apply1DsMappingModalDisplayReducer,
    apply1DsKeywordMappingModalDisplay: apply1DsKeywordMappingModalDisplayReducer,
    tagTypeListLoading: tagTypeListLoadingReducer,
    confirmModalDisplay: confirmModalReducer,
    showRevstackPopup: showRevstackPopupReducer,

    mainFilters: mainFiltersReducer,
    relativeFilters: relativeFiltersReducer,
    mainFiltersData: mainFiltersDataReducer,
    revstackLayoutFilters: revstackLayoutFiltersReducer,
    revstackWidgetData: revstackWidgetDataReducer,
    aiInsightsPayload: aiInsightsPayloadReducer,
    selectedDateRange: selectedDateRangeReducer,
    revstackChannel: revstackChannelReducer,
    revstackChannelsList: revstackChannelsListReducer,
    aiInsightsData: aiInsightsDataReducer,
    drilldownFilters: drillDownFiltersReducer,
    trendsFilters: trendsFiltersReducer,
    revstackActiveTab: revstackActiveTabReducer,
    isAiInsightsLoading: isAiInsightsLoadingReducer,
    revstackDashboardTabs: revstackDashboardTabsReducer,
    campaignCreationDisplay: campaignCreationDisplayReducer,
    clusters: clustersReducer,
    skuMainFilters: skuMainFiltersReducer,
    additionalFilters: additionalFiltersReducer,
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            immutableCheck: false,
            serializableCheck: false,
        }),
});

export default store;
